import { useState, useEffect, useCallback, useMemo } from 'react';
import BotApi from '../services/botApi';
import websocketService from '../services/websocketService';

/**
 * Hook for managing bot chat state
 */
export function useBotChat(conversationId, token, apiUrl) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const api = useMemo(() => new BotApi(apiUrl), [apiUrl]);

  // Load conversation
  useEffect(() => {
    if (!conversationId || !token) return;

    const loadConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getConversation(conversationId, token);
        setConversation(data);
        setMessages(data.conversation_thread || []);
        setStatus(data.status);
      } catch (err) {
        setError(err.message);
        console.error('Error loading conversation:', err);
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [conversationId, token, api]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!conversationId || !token || status === 'resolved') return;

    // Connect to WebSocket
    websocketService.connect(conversationId, token);

    // Listen for new messages
    const unsubscribeMessage = websocketService.on('message:new', (data) => {
      if (data.conversationId === conversationId) {
        // Add new message to messages array
        if (data.message) {
          setMessages(prev => {
            // Check if message already exists (avoid duplicates)
            const exists = prev.some(msg => 
              msg.content === data.message.content && 
              msg.timestamp === data.message.timestamp
            );
            if (exists) return prev;
            return [...prev, data.message];
          });

          // Update status if provided
          if (data.status) {
            setStatus(data.status);
          }
          
          // Update conversation metadata if provided (without full reload)
          if (data.conversation) {
            setConversation(prev => ({ ...prev, ...data.conversation }));
          }
        }
      }
    });

    // Listen for conversation updates
    const unsubscribeUpdate = websocketService.on('conversation:updated', (data) => {
      if (data.conversationId === conversationId) {
        // Update status
        if (data.status) {
          setStatus(data.status);
        }
        
        // Update conversation metadata if provided (without full reload)
        if (data.conversation) {
          setConversation(prev => ({ ...prev, ...data.conversation }));
        }
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribeMessage();
      unsubscribeUpdate();
      // Don't disconnect here - let ChatContext manage the connection lifecycle
    };
  }, [conversationId, token, status, api]);

  // Send message
  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      // Optimistically add user message
      const userMessage = {
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);

      // Send to API
      const result = await api.sendMessage(conversationId, token, message);

      // Update status
      if (result.status) {
        setStatus(result.status);
      }

      // Only add bot message if there is one (null means no response, e.g., casual acknowledgment)
      if (result.botMessage) {
        const botMessage = {
          role: 'bot',
          content: result.botMessage,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => {
          // Check if already exists (from WebSocket)
          const exists = prev.some(msg => 
            msg.content === botMessage.content && 
            msg.timestamp === botMessage.timestamp
          );
          if (exists) return prev;
          return [...prev, botMessage];
        });
      }

      // Update conversation if provided
      if (result.conversation) {
        setConversation(result.conversation);
        // Only update messages if thread is provided and different
        if (result.conversation.conversation_thread) {
          setMessages(result.conversation.conversation_thread);
        }
      }

      return result;
    } catch (err) {
      setError(err.message);
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1));
      throw err;
    } finally {
      setSending(false);
    }
  }, [conversationId, token, sending, api]);

  return {
    conversation,
    messages,
    loading,
    sending,
    error,
    status,
    sendMessage
  };
}

